import { FC, ReactNode, Suspense, useEffect, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, useRefetchableFragment } from 'react-relay/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { backofficeUrl, isDraftProduct, T, toGlobalID, toLocalID, uuid } from 'src/shared/helpers';
import OpenInNewIcon from 'src/shared/material_icons/action/open_in_new.svg';
import { default as CogIcon, default as SettingsIcon } from 'src/shared/material_icons/action/settings.svg';
import WarningTriangleIcon from 'src/shared/material_icons/alert/warning_amber.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import LinkOffIcon from 'src/shared/material_icons/content/link_off.svg';
import VerticalThreePoint from 'src/shared/material_icons/navigation/more_vert.svg';
import styled from 'styled-components';
import { Badge } from '../../widgets/badge';
import { BasicButton, GhostButton, Link, PrimaryButton, TextButton } from '../../widgets/buttons';
import { CustomErrorBoundary } from '../../widgets/customErrorBoundary';
import { Dialog, DialogWindow, MessageDialog, QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { DropdownActionItem, DropdownActions } from '../../widgets/dropdown';
import { EmptyList } from '../../widgets/emptyList';
import { Icon } from '../../widgets/icon';
import { useInlineHelp } from '../../widgets/inlineHelpLayout';
import { ContainerBase } from '../../widgets/layouts';
import { NotificationBanner } from '../../widgets/notificationBanner';
import { removeNotification, showErrorNotification, showNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import { PageHeader } from '../../widgets/pageHeader';
import { Section } from '../../widgets/section';
import { SectionContent } from '../../widgets/section/sectionContent';
import { Spinner } from '../../widgets/spinner';
import Stack from '../../widgets/stack/stack';
import { Row, Table, TableHeading } from '../../widgets/table';
import { Tab, TabList, TabPanel, ZTabs } from '../../widgets/ztabs';
import { ZTooltip } from '../../widgets/ztooltip';
import SalesChannelIcon from '../products/salesChannelIcon';
import { renameSalesChannelDialogMutation } from './__generated__/renameSalesChannelDialogMutation.graphql';
import { salesChannelConnectModelMutation } from './__generated__/salesChannelConnectModelMutation.graphql';
import { salesChannelDisconnectTaskPollerQuery } from './__generated__/salesChannelDisconnectTaskPollerQuery.graphql';
import { salesChannelProductsAtRiskQuery } from './__generated__/salesChannelProductsAtRiskQuery.graphql';
import { ECommerce, salesChannelsActive$data, salesChannelsActive$key } from './__generated__/salesChannelsActive.graphql';
import { salesChannelSetStatusMutation } from './__generated__/salesChannelSetStatusMutation.graphql';
import { salesChannelsQuery } from './__generated__/salesChannelsQuery.graphql';

import type { PartialSalesChannel } from 'src/components/pages/productConfiguration/steps/6-done/connectModelDialog';
import ConnectModelDialog from 'src/components/pages/productConfiguration/steps/6-done/connectModelDialog';
import Loader from 'src/components/widgets/loader';
import ShopifyAppEmbedBanner, {
    getZakekeOffLabel,
    getZakekeOffTooltip,
    isSameSalesChannel,
    scrollToShopifyAppEmbedBanner,
    usePendingAppEmbeds
} from 'src/components/widgets/shopifyAppEmbedBanner/shopifyAppEmbedBanner';
import { useAppState } from 'src/shared/appState';
import config from 'src/shared/config';
import { useFeatureFlag, useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import usePermissions from 'src/shared/hooks/usePermissions';
import { Center } from '../../../shared/globals';
import CreateStandaloneSiteDialog from '../settings/standaloneSites/createStandaloneSiteDialog';
import { getStandaloneSitePublicUrl } from '../settings/standaloneSites/standaloneSiteNavigation';
import { salesChannelDeleteMiniSiteMutation } from './__generated__/salesChannelDeleteMiniSiteMutation.graphql';
import { salesChannelMiniSitesQuery } from './__generated__/salesChannelMiniSitesQuery.graphql';
import { salesChannelRenameMiniSiteMutation } from './__generated__/salesChannelRenameMiniSiteMutation.graphql';
import AddSalesChannelDialog from './addSalesChannelDialog';
import ApiIntegrationSalesChannelDialog from './apiIntegrationSalesChannelDialog';
import ApiKeyDialog from './apiKeyDialog';
import CCloudIntegrationSalesChannelDialog from './cCloudIntegrationSalesChannelDialog';
import { generateStoreNumbersByPosition } from './helpers';
import InlineRenameCell from './inlineRenameCell';
import { updateNameSalesChannel } from './renameSalesChannelDialog';
import StoreUrlSalesChannelDialog from './storeUrlSalesChannelDialog';
import type { salesChannelSwitchQueriesCheckShopifyTestStoreQuery } from './switch/__generated__/salesChannelSwitchQueriesCheckShopifyTestStoreQuery.graphql';
import { checkShopifyTestStoreQuery } from './switch/salesChannelSwitchQueries';
import { SalesChannelSwitchTasksPanel } from './switch/salesChannelSwitchTasks';
import SetAsMasterActionItem from './switch/setAsMasterActionItem';
import { CREATE_CUSTOM_PLAN_FOR_SELLER_TASK_TYPE, isTaskPending, TASKSTATUS } from './switch/switchTypes';
import { useSalesChannelSwitch } from './switch/useSalesChannelSwitch';

const ZakekeOffBadgeButton = styled.button`
    display: inline-flex;
    border: 0;
    background: none;
    padding: 0;
    cursor: pointer;
`;

const StyledImg = styled.div`
    > div {
        width: 32px;
        height: 32px;
    }

    img {
        width: 30px;
        height: 30px;
    }
`;

const ActionsColContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

const StyledLinkContainer = styled.div`
    word-wrap: nowrap;
`;

const StyledDivElementTable = styled.div`
    align-content: center;
    max-width: 100%;
`;

const StyledLink = styled(Link)`
    font-weight: 400;

    span {
        word-break: break-all;
    }
`;

const StoreNumberCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
`;

// The badges sit beside the store number and share a single wrapping column: when the row runs out of
// room they wrap within it, so every wrapped badge lines up under the first badge rather than overflowing.
const StoreBadges = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    flex: 1;
    min-width: 0;
    gap: 6px 8px;
`;

const ShopifyDevelopmentBadge: FC<{ salesChannelID: string }> = ({ salesChannelID }) => {
    const data = useLazyLoadQuery<salesChannelSwitchQueriesCheckShopifyTestStoreQuery>(
        checkShopifyTestStoreQuery,
        { salesChannelID: toLocalID(salesChannelID) },
        { fetchPolicy: 'store-or-network' }
    );

    return data.shopifyAuthEcommerceSettings?.isTestStore ? <Badge variant='warning'>{T._('Development', 'Admin')}</Badge> : null;
};

const SectionHeaderActionsGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

const HeaderActionsDivider = styled.div`
    width: 1px;
    align-self: stretch;
    min-height: 24px;
    background: ${(props) => props.theme.app.borderColor};
`;

// Tighten the gap between the section header/description and the table below it.
const CompactSection = styled(Section)`
    ${SectionContent} {
        padding-top: 8px;
    }
`;

// Pull the empty state up so it stays visible even when the online stores table above is tall.
const CompactEmptyState = styled.div`
    > div {
        padding-top: 8px;
    }
`;

// Shared column template so the Online Stores and Mini-Sites tables line up column-for-column.
const SALES_CHANNEL_TABLE_WIDTHS = ['minmax(225px, 1.05fr)', '1.1fr', 'minmax(auto, 500px)', '0.6fr', '0.9fr'];

const salesChannelMiniSitesQueryNode = graphql`
    query salesChannelMiniSitesQuery {
        standaloneSites {
            id
            standaloneSiteID
            name
            domain
            status
        }
    }
`;

const salesChannelRenameMiniSiteMutationNode = graphql`
    mutation salesChannelRenameMiniSiteMutation($input: StandaloneSiteUpdateInput!) {
        updateStandaloneSite(input: $input) {
            id
            name
        }
    }
`;

const salesChannelDeleteMiniSiteMutationNode = graphql`
    mutation salesChannelDeleteMiniSiteMutation($id: ID!) {
        deleteStandaloneSite(id: $id)
    }
`;

const renderMiniSiteStatusBadge = (status: string) => {
    switch (status) {
        case 'ACTIVE':
            return <Badge variant='success'>{T._('Active', 'Admin')}</Badge>;
        case 'DRAFT':
            return <Badge variant='warning'>{T._('Draft', 'Admin')}</Badge>;
        case 'DISABLED':
            return <Badge variant='danger'>{T._('Disabled', 'Admin')}</Badge>;
        default:
            return <Badge variant='neutral'>{status}</Badge>;
    }
};

const MiniSitesList: FC<{ fetchKey: number; onChanged: () => void; onAdd: () => void }> = ({ fetchKey, onChanged, onAdd }) => {
    const navigate = useNavigate();
    const { showDialog, closeDialog } = useDialogManager();
    const data = useLazyLoadQuery<salesChannelMiniSitesQuery>(salesChannelMiniSitesQueryNode, {}, { fetchKey, fetchPolicy: 'network-only' });
    const sites = data.standaloneSites || [];

    const [renameSite] = useMutation<salesChannelRenameMiniSiteMutation>(salesChannelRenameMiniSiteMutationNode);
    const [deleteSite] = useMutation<salesChannelDeleteMiniSiteMutation>(salesChannelDeleteMiniSiteMutationNode);

    const handleRename = (id: string, newName: string) => {
        renameSite({
            variables: { input: { id, name: newName } },
            onError: () => {
                showErrorNotification({ message: T._('Failed to rename mini-site.', 'Admin') });
            },
            onCompleted: () => {}
        });
    };

    const goToSettings = (standaloneSiteID: string) => navigate(backofficeUrl(`/mini-sites/${encodeURIComponent(standaloneSiteID)}/settings`));
    const goToTheme = (standaloneSiteID: string) => navigate(backofficeUrl(`/mini-sites/${encodeURIComponent(standaloneSiteID)}/themes`));

    const handleDelete = (site: { id: string; name: string }) => {
        showDialog(
            'delete-mini-site',
            <QuestionDialog
                showCloseButton={false}
                title={T._('Delete mini-site', 'Admin')}
                message={T._(`Are you sure you want to delete "${site.name}"? This action cannot be undone.`, 'Admin')}
                buttonYesLabel={T._('Cancel', 'Admin')}
                buttonNoLabel={T._('Delete', 'Admin')}
                destructiveNo
                onYesClick={() => closeDialog('delete-mini-site')}
                onNoClick={() => {
                    closeDialog('delete-mini-site');
                    deleteSite({
                        variables: { id: site.id },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Mini-site deleted.', 'Admin') });
                            onChanged();
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed to delete mini-site.', 'Admin') });
                        },
                        updater: (store) => store.invalidateStore()
                    });
                }}
            />
        );
    };

    if (sites.length === 0) {
        return (
            <CompactSection title={T._('Mini-Sites', 'Admin')}>
                <CompactEmptyState>
                    <EmptyList
                        hideIcon
                        title={''}
                        subTitle={T._(
                            'Create a branded mini-site powered by Zakeke, accessible directly on the web\nor embeddable in a physical kiosk, POS or similar solutions.',
                            'Admin'
                        )}
                        learnMoreText={T._('Learn more', 'Admin')}
                        learnMoreLink='https://zakeke.zendesk.com/hc/en-us'
                        buttonTitle={T._('Add', 'Admin')}
                        onButtonClick={onAdd}
                    />
                </CompactEmptyState>
            </CompactSection>
        );
    }

    const headings = [
        ['#', SALES_CHANNEL_TABLE_WIDTHS[0]],
        [T._('Name', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[1]],
        [T._('Url', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[2]],
        [T._('Status', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[3]],
        ['', SALES_CHANNEL_TABLE_WIDTHS[4]]
    ] as TableHeading[];

    return (
        <CompactSection
            title={T._('Mini-Sites', 'Admin')}
            description={T._(
                'Launch standalone customizer portals for B2B sales, kiosks, showrooms, POS, and other experiences that do not require Zakeke embedded in an online store.',
                'Admin'
            )}
            headerActions={
                <PrimaryButton $size='medium' onClick={onAdd} style={{ minWidth: '160px' }}>
                    <Icon>
                        <PlusIcon />
                    </Icon>
                    {T._('Add Mini-Site', 'Admin')}
                </PrimaryButton>
            }
        >
            <Table headings={headings} compact>
                {sites.map((site, i) => {
                    const publicUrl = getStandaloneSitePublicUrl(site.domain);
                    return (
                        <Row
                            key={site.id}
                            cells={[
                                <StyledDivElementTable>{T._(`Mini-Site ${i + 1}`, 'Admin')}</StyledDivElementTable>,
                                <InlineRenameCell name={site.name} onRename={(newName) => handleRename(site.id, newName)} />,
                                <StyledDivElementTable>
                                    {publicUrl ? (
                                        <StyledLinkContainer>
                                            <StyledLink href={publicUrl} target='_blank' title={publicUrl}>
                                                <span>{publicUrl}</span>
                                            </StyledLink>
                                        </StyledLinkContainer>
                                    ) : (
                                        '-'
                                    )}
                                </StyledDivElementTable>,
                                <StyledDivElementTable>{renderMiniSiteStatusBadge(site.status)}</StyledDivElementTable>,
                                <ActionsColContainer>
                                    <ZTooltip position={'top'} title={T._('Settings', 'Admin')}>
                                        <GhostButton
                                            $size='small'
                                            $type='icon'
                                            aria-label={T._('Settings', 'Admin')}
                                            onClick={() => goToSettings(site.standaloneSiteID.toString())}
                                        >
                                            <Icon>
                                                <SettingsIcon />
                                            </Icon>
                                        </GhostButton>
                                    </ZTooltip>
                                    <DropdownActions
                                        header={
                                            <Icon $hoverable>
                                                <VerticalThreePoint />
                                            </Icon>
                                        }
                                        orientation={'RIGHT'}
                                        sections={[
                                            [
                                                <DropdownActionItem
                                                    key='view'
                                                    disabled={!publicUrl}
                                                    onClick={() => publicUrl && window.open(publicUrl, '_blank')}
                                                >
                                                    {T._('View mini-site', 'Admin')}
                                                </DropdownActionItem>,
                                                <DropdownActionItem key='settings' onClick={() => goToSettings(site.standaloneSiteID.toString())}>
                                                    {T._('Settings', 'Admin')}
                                                </DropdownActionItem>,
                                                <DropdownActionItem key='theme' onClick={() => goToTheme(site.standaloneSiteID.toString())}>
                                                    {T._('Manage theme', 'Admin')}
                                                </DropdownActionItem>
                                            ],
                                            [
                                                <DropdownActionItem key='delete' danger onClick={() => handleDelete(site)}>
                                                    {T._('Delete', 'Admin')}
                                                </DropdownActionItem>
                                            ]
                                        ]}
                                    />
                                </ActionsColContainer>
                            ]}
                        />
                    );
                })}
            </Table>
        </CompactSection>
    );
};

const UnclickableDisconnectButtonContainer = styled.div`
    display: flex;
    align-items: center;
`;

const MasterDisconnectTooltipContent = styled(Stack)`
    text-align: left;
`;

const MasterDisconnectTooltip: FC = () => (
    <MasterDisconnectTooltipContent $gap='12px'>
        <div>
            {T._('You cannot disconnect the Master Integration directly. If you need to disconnect it, contact us at', 'Admin')}{' '}
            <Link href='mailto:help@zakeke.com'>help@zakeke.com</Link>.
        </div>
        <div>
            <strong>{T._('Replacing your Master Integration?', 'Admin')}</strong>{' '}
            {T._(
                "You don't need to disconnect the current one first. Add the new integration as a Sales Channel and complete its setup. Before completing the overall process, you can replace the old Master Integration with the new one, leaving only the new integration as Master. Alternatively, you can keep both integrations connected and switch the Master Integration to another Sales Channel at a later time.",
                'Admin'
            )}
        </div>
    </MasterDisconnectTooltipContent>
);

export const SalesChannelNotAvailableDialogWindow = styled(DialogWindow)`
    p {
        font-size: 14px;
    }

    max-width: 600px;
`;

// Slightly wider than the default 'small' dialog (600px) to give the disconnect
// modal more room for the product list and the "how to disconnect" steps.
const DisconnectDialogWindow = styled(DialogWindow)`
    flex-basis: 780px;
    max-width: 780px;
`;

const TabListContainer = styled.div`
    display: flex;
    align-items: left;
`;

const StyledContainerBase = styled(ContainerBase)<{ showBackButton: boolean }>`
    padding-top: ${(props) => (props?.showBackButton ? '0' : '60px')};
`;

const HeroSection = styled.div`
    text-align: center;
    padding: 48px 24px 40px;
`;

const HeroTitle = styled.h2`
    font-size: 22px;
    font-weight: ${(props) => props.theme.font.semi_bold};
    color: ${(props) => props.theme.app.textColor};
    margin: 0 0 12px;
`;

const HeroDescription = styled.p`
    font-size: 14px;
    color: ${(props) => props.theme.app.primary};
    margin: 0 auto;
    max-width: 520px;
    line-height: 1.6;
`;

const ChannelCardsGrid = styled.div<{ $singleColumn?: boolean }>`
    display: grid;
    grid-template-columns: ${(props) => (props.$singleColumn ? 'minmax(0, 450px)' : '1fr 1fr')};
    justify-content: center;
    gap: 24px;
    max-width: 900px;
    margin: 0 auto;
    padding: 0 24px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const ChannelCard = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    padding: 28px;
    position: relative;
    background: ${(props) => props.theme.app.backgroundColor};
    display: flex;
    flex-direction: column;
`;

const CardBadge = styled.span<{ $variant: 'store' | 'portal' }>`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: ${(props) => props.theme.font.semi_bold};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: ${(props) => (props.$variant === 'store' ? 'rgba(232, 101, 10, 0.1)' : 'rgba(2, 136, 209, 0.1)')};
    color: ${(props) => (props.$variant === 'store' ? props.theme.app.secondary : props.theme.app.secondary_alt)};
    align-self: flex-start;
`;

const CardTitle = styled.h3`
    font-size: 18px;
    font-weight: ${(props) => props.theme.font.semi_bold};
    color: ${(props) => props.theme.app.textColor};
    margin: 16px 0 8px;
`;

const CardText = styled.p`
    font-size: 14px;
    color: ${(props) => props.theme.app.primary};
    margin: 0 0 20px;
    line-height: 1.5;
    /* Keep the description height consistent so both feature lists start at the same height */
    min-height: 84px;

    @media (max-width: 768px) {
        min-height: 0;
    }
`;

const FeatureList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 24px;
`;

const FeatureItem = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${(props) => props.theme.app.textColor};
    padding: 4px 0;

    &::before {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${(props) => props.theme.app.component.notificationBanner.success.iconContainerBGColor};
        flex-shrink: 0;
    }
`;

const CardAction = styled.div`
    margin-top: auto;
`;

const FullWidthPrimaryButton = styled(PrimaryButton)`
    width: 100%;
`;

const InfoBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: ${(props) => props.theme.app.pageBackgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    padding: 16px 24px;
    /* Matches the cards row width above (grid max-width 900px minus its 24px side padding) */
    max-width: 852px;
    margin: 32px auto 0;
    gap: 16px;

    @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
    }
`;

const InfoBannerContent = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const InfoIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(2, 136, 209, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${(props) => props.theme.app.secondary_alt};
    font-size: 16px;
    font-weight: ${(props) => props.theme.font.semi_bold};
`;

const InfoBannerTextBlock = styled.div`
    font-size: 13px;
    color: ${(props) => props.theme.app.primary};
`;

export const activeSalesChannelFragment = graphql`
    fragment salesChannelsActive on Query @refetchable(queryName: "salesChannelActiveFragmentQuery") {
        user {
            userID
            email
        }
        salesChannels(status: ACTIVE) {
            id
            detail {
                id
                properties {
                    name
                    storePosition
                }
            }
            ecommerceSettings {
                id
                ecommerceGlobalID
                ecommerceID
                ecommerceName
                remoteUrl
                isMarketplace
                definitiveUrl
                currencyID
                currencyCode
            }
        }
        productSwitcherTasks {
            taskID
            state
            taskType
            lastError
            elapsedTime
            modifiedOn
            extraData {
                taskID
                masterSalesChannelID
                secondarySalesChannelID
                delayMinutes
                paymentMethodSetupTaskID
                sourceSalesChannelID
                destinationSalesChannelID
                isSourceSalesChannelDisabled
            }
        }
        isSellerMigrating
    }
`;

const DisabledSalesChannelContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    cursor: not-allowed;
`;

const DisconnectDialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 12px;
`;

const DisconnectHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    /* Leave room for the dialog close button in the top-right corner. */
    padding-right: 24px;
`;

const DisconnectHeaderTitle = styled.h1`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
    color: ${(props) => props.theme.app.textColor};
`;

const DisconnectHeaderMeta = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 13px;
    color: ${(props) => props.theme.app.primary};
`;

const LinkPanel = styled.div`
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
`;

const LinkPanelHeader = styled.div`
    display: grid;
    gap: 2px;
    padding: 12px 14px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.pageBackgroundColor};
`;

const LinkPanelTitle = styled.strong`
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const LinkPanelFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 10px 14px;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
`;

const DisconnectInlineLink = styled.button`
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    color: ${(props) => props.theme.app.secondary};
    text-decoration: underline;
    cursor: pointer;
`;

const ProductList = styled.div`
    max-height: 260px;
    overflow-y: auto;
`;

const ProductItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};

    &:last-child {
        border-bottom: none;
    }
`;

const ProductThumb = styled.div<{ $src?: string | null }>`
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 6px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    background-image: ${(props) => (props.$src ? `url("${props.$src}")` : 'none')};
    background-size: cover;
    background-position: center;
`;

const ProductInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 2px;
`;

const ProductName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 500;
    color: ${(props) => props.theme.app.textColor};
`;

const ProductMeta = styled.span`
    font-size: 12px;
    color: ${(props) => props.theme.app.primary};
`;

const LinkProductButton = styled(BasicButton)`
    flex-shrink: 0;
    white-space: nowrap;
`;

const StepsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const StepDivider = styled.div`
    height: 1px;
    background: ${(props) => props.theme.app.borderColor};
`;

const Step = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const StepNumber = styled.div`
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: ${(props) => props.theme.app.primary_dark};
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
`;

const StepBody = styled.div`
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 8px;
`;

const StepSplit = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
`;

const StepText = styled.div`
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 8px;
`;

const StepTitle = styled.strong`
    font-size: 15px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const StepTitleMuted = styled.span`
    font-weight: 400;
    color: ${(props) => props.theme.app.primary};
`;

const StepDescription = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${(props) => props.theme.app.primary};
`;

const OpenAdminButton = styled(BasicButton)`
    flex-shrink: 0;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    svg {
        width: 16px;
        height: 16px;
    }
`;

const DisconnectConfirmationMessage = styled.p`
    margin: 0;
    color: ${(props) => props.theme.app.textColor};
    font-size: 14px;
    line-height: 1.5;
`;

const DisconnectDialogLoadingState = styled(Center)`
    flex-direction: column;
    gap: 12px;
    min-height: 120px;
    color: ${(props) => props.theme.app.textColor};
    font-size: 13px;
`;

const DisconnectDialogLoader: FC = () => (
    <DisconnectDialogLoadingState role='status' aria-live='polite'>
        <Spinner />
        <span>{T._('Checking affected products...', 'Admin')}</span>
    </DisconnectDialogLoadingState>
);

const MAX_PRODUCT_NAME_LENGTH = 50;
const DISCONNECT_SALES_CHANNEL_TASK_TYPE = 'DISCONNECT_SALES_CHANNEL';
const DISCONNECT_SALES_CHANNEL_POLLING_INTERVAL_MS = 5000;

const truncateProductName = (name: string) => (name.length > MAX_PRODUCT_NAME_LENGTH ? `${name.slice(0, MAX_PRODUCT_NAME_LENGTH)}...` : name);

const SalesChannelDisconnectTaskPoller: FC<{
    taskID: number;
    onFinished: (state: number, lastError: string | null | undefined) => void;
}> = ({ taskID, onFinished }) => {
    const [fetchKey, setFetchKey] = useState(0);
    const data = useLazyLoadQuery<salesChannelDisconnectTaskPollerQuery>(
        graphql`
            query salesChannelDisconnectTaskPollerQuery($id: ID!) {
                productSwitcherTask(id: $id) {
                    taskID
                    state
                    lastError
                }
            }
        `,
        { id: toGlobalID(taskID, 'ProductSwitcherTask') },
        { fetchKey, fetchPolicy: 'network-only' }
    );

    const task = data.productSwitcherTask;

    useEffect(() => {
        if (task?.state === TASKSTATUS.Completed || task?.state === TASKSTATUS.Error) {
            onFinished(task.state, task.lastError);
            return;
        }

        const intervalID = window.setInterval(() => setFetchKey((previousFetchKey) => previousFetchKey + 1), DISCONNECT_SALES_CHANNEL_POLLING_INTERVAL_MS);
        return () => window.clearInterval(intervalID);
    }, [onFinished, task?.lastError, task?.state]);

    return null;
};

interface SalesChannelDisconnectWarningProps {
    salesChannelID: string;
    storeLabel: string;
    /** Header title, e.g. "Store 2". Falls back to the store name/label when omitted. */
    storeTitle?: string;
    /** Display name of the store, shown in the header meta row. */
    storeName?: string;
    /** Store domain (e.g. my-store.myshopify.com), shown in the header meta row. */
    storeDomain?: string;
    disconnectByUninstallingApp?: boolean;
}

const stripUrlScheme = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

// Builds the Shopify admin URL for a store from its *.myshopify.com domain
// (e.g. ciccio-sc.myshopify.com -> https://admin.shopify.com/store/ciccio-sc).
// Falls back to the generic admin entry point when no handle can be resolved.
const getShopifyAdminUrl = (...candidateUrls: (string | null | undefined)[]) => {
    for (const candidateUrl of candidateUrls) {
        if (!candidateUrl) continue;
        const host = stripUrlScheme(candidateUrl).split('/')[0];
        const match = host.match(/^([a-z0-9-]+)\.myshopify\.com$/i);
        if (match) return `https://admin.shopify.com/store/${match[1]}`;
    }
    return 'https://admin.shopify.com';
};

const DisconnectDangerBanner: FC<{ title: ReactNode; children: ReactNode }> = ({ title, children }) => (
    <NotificationBanner type='WARNING' icon={<WarningTriangleIcon />} title={title} content={children} />
);

const SalesChannelDisconnectHeader: FC<SalesChannelDisconnectWarningProps> = ({ salesChannelID, storeLabel, storeName, storeDomain, storeTitle }) => {
    const title = storeTitle || storeName || storeLabel;
    const metaName = storeName || storeLabel;
    const domain = storeDomain ? stripUrlScheme(storeDomain) : '';

    return (
        <DisconnectHeader>
            <DisconnectHeaderTitle>
                {title ? (
                    <>
                        {T._('Before you disconnect', 'Admin')} {title}
                    </>
                ) : (
                    T._('Disconnect sales channel', 'Admin')
                )}
            </DisconnectHeaderTitle>
            <DisconnectHeaderMeta>
                {metaName && <span>{metaName}</span>}
                {metaName && domain && <span>·</span>}
                {domain && <span>{domain}</span>}
                <Suspense fallback={null}>
                    <ShopifyDevelopmentBadge salesChannelID={salesChannelID} />
                </Suspense>
            </DisconnectHeaderMeta>
        </DisconnectHeader>
    );
};

const SalesChannelDisconnectDetails: FC<SalesChannelDisconnectWarningProps> = ({
    salesChannelID,
    storeLabel,
    storeName,
    storeDomain,
    disconnectByUninstallingApp = false
}) => {
    // Bumped after a product is successfully linked to another store, so the at-risk list refetches
    // and the just-linked product drops out (and the count updates).
    const [fetchKey, setFetchKey] = useState(0);
    const data = useLazyLoadQuery<salesChannelProductsAtRiskQuery>(
        graphql`
            query salesChannelProductsAtRiskQuery($salesChannelsIDs: [String!]) {
                models(first: 10000, salesChannelsIDs: $salesChannelsIDs) {
                    edges {
                        node {
                            ... on Model {
                                id
                                code
                                name
                                smallImageUrl
                                isDemo
                                configurationStatus
                                salesChannel {
                                    id
                                }
                                additionalCodes {
                                    salesChannelID
                                    code
                                    merchantProductName
                                }
                            }
                        }
                    }
                }
                salesChannels(status: ACTIVE) {
                    id
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                    ecommerceSettings {
                        definitiveUrl
                        ecommerceID
                        ecommerceName
                        hasProductsApi
                        isMarketplace
                        remoteUrl
                    }
                }
            }
        `,
        { salesChannelsIDs: [salesChannelID] },
        { fetchKey, fetchPolicy: 'network-only' }
    );

    const sourceSalesChannelID = toLocalID(salesChannelID);
    const isMasterIntegration = sourceSalesChannelID === 1;
    const storeDisplayName = storeName || storeLabel;
    const activeSalesChannelIDs = new Set(data.salesChannels.map((salesChannel) => toLocalID(salesChannel.id)).filter((id): id is number => id != null));
    const products = (data.models?.edges ?? [])
        .flatMap((edge) => (edge?.node ? [edge.node] : []))
        .filter((product) => isMasterIntegration || toLocalID(product.salesChannel.id) === sourceSalesChannelID)
        .map((product) => ({
            ...product,
            willBreak: isMasterIntegration,
            willBeDeleted:
                !isMasterIntegration &&
                !product.additionalCodes.some((productCode) => {
                    const productCodeSalesChannelID = toLocalID(productCode.salesChannelID);
                    return (
                        productCodeSalesChannelID != null &&
                        productCodeSalesChannelID !== sourceSalesChannelID &&
                        activeSalesChannelIDs.has(productCodeSalesChannelID)
                    );
                })
        }))
        .filter((product) => isMasterIntegration || product.willBeDeleted);

    const { showDialog, closeDialog } = useDialogManager();
    const { isAllowed } = usePermissions();
    const navigate = useNavigate();
    const [commitConnectModel] = useMutation<salesChannelConnectModelMutation>(graphql`
        mutation salesChannelConnectModelMutation($id: ID!, $target: ModelDuplicateTargetInput!) {
            connectModel(id: $id, target: $target)
        }
    `);

    // Opens the Customizable Products page pre-filtered to exactly the products that would be deleted
    // by this disconnect (scoped to the store via scId, then narrowed to their IDs via deletableIds).
    const handleViewDeletableProducts = () => {
        const deletableIDs = products
            .filter((product) => product.willBeDeleted)
            .map((product) => toLocalID(product.id))
            .filter((id): id is number => id != null);

        if (deletableIDs.length === 0) return;

        closeDialog('disconnect-channel');
        navigate(`${backofficeUrl('/products')}?scId=${encodeURIComponent(salesChannelID)}&deletableIds=${deletableIDs.join(',')}`);
    };

    // The online sales channels a product can be connected to (excludes the internal "NONE" channel).
    const realSalesChannels: PartialSalesChannel[] = (data.salesChannels ?? [])
        .filter((salesChannel) => salesChannel.ecommerceSettings?.ecommerceName !== 'NONE')
        .map((salesChannel) => ({
            id: salesChannel.id,
            detail: salesChannel.detail
                ? { id: salesChannel.detail.id, properties: salesChannel.detail.properties ? { name: salesChannel.detail.properties.name } : null }
                : null,
            ecommerceSettings: salesChannel.ecommerceSettings
                ? {
                      definitiveUrl: salesChannel.ecommerceSettings.definitiveUrl ?? null,
                      ecommerceID: salesChannel.ecommerceSettings.ecommerceID,
                      ecommerceName: salesChannel.ecommerceSettings.ecommerceName,
                      hasProductsApi: salesChannel.ecommerceSettings.hasProductsApi,
                      isMarketplace: salesChannel.ecommerceSettings.isMarketplace,
                      remoteUrl: salesChannel.ecommerceSettings.remoteUrl ?? null
                  }
                : null
        }));

    // Mirrors the "Add to an Online Store" action from the products table (productList.tsx):
    // it opens ConnectModelDialog to connect + publish the product to another sales channel.
    const handleAddToOnlineStore = (product: (typeof products)[number]) => {
        if (product.isDemo) return;
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        // A draft (incomplete) product cannot be connected to a sales channel until its configuration is completed.
        if (isDraftProduct(product.configurationStatus)) {
            showErrorNotification({ message: T._('Complete the product configuration before connecting it to a sales channel.', 'Admin') });
            return;
        }

        const availableSalesChannels = realSalesChannels.filter(
            (salesChannel) => salesChannel.id !== product.salesChannel.id && !product.additionalCodes.some((code) => code.salesChannelID === salesChannel.id)
        );

        if (availableSalesChannels.length === 0) {
            showNotification({
                type: 'danger',
                message: T._('The product has been connected with all sales channels.', 'Admin'),
                dismiss: { duration: 3000 }
            });
            return;
        }

        showDialog(
            'connectProductDialog',
            <Suspense
                fallback={
                    <Center>
                        <Loader />
                    </Center>
                }
            >
                <ConnectModelDialog
                    productID={'guid://Zakeke/' + uuid() + '/' + toLocalID(product.id)}
                    forEditProduct={false}
                    forAddingNewSalesChannel={true}
                    merchantProductOptionsConnected={[]}
                    additionalCodes={product.additionalCodes.map((code) => ({
                        code: code.code,
                        salesChannelID: code.salesChannelID,
                        merchantProductName: code.merchantProductName ?? ''
                    }))}
                    productColors={[]}
                    salesChannels={availableSalesChannels}
                    productSalesChannelID={product.salesChannel.id}
                    onConfirm={async (sourceProduct, salesChannelId) => {
                        if (!sourceProduct) return;

                        closeDialog('connectProductDialog');

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._('Publishing your customizable product...', 'Admin')
                        });

                        try {
                            await new Promise<void>((resolve, reject) =>
                                commitConnectModel({
                                    onCompleted: () => resolve(),
                                    onError: (error) => reject(error),
                                    updater: (store) => {
                                        store.invalidateStore();
                                    },
                                    variables: {
                                        id: product.id,
                                        target: {
                                            name: sourceProduct.name,
                                            sku: sourceProduct.sku,
                                            imageUrl: sourceProduct.thumbnail,
                                            salesChannelId
                                        }
                                    }
                                })
                            );

                            showNotification({
                                type: 'success',
                                message: T._('Product connected and published!', 'Admin'),
                                dismiss: { duration: 3000 }
                            });

                            // Refetch the at-risk list: the product now lives on another channel, so it
                            // drops out of the list and the deletion count updates.
                            setFetchKey((previousFetchKey) => previousFetchKey + 1);
                        } catch (error: any) {
                            showNotification({
                                type: 'danger',
                                message: (
                                    <>
                                        {T._('Failed connecting and publishing product.', 'Admin')}
                                        <br />
                                        {error?.errors?.[0]?.extensions?.message}
                                    </>
                                ),
                                dismiss: { duration: 3000 }
                            });
                        }

                        removeNotification(loadingNotification);
                    }}
                />
            </Suspense>
        );
    };

    if (products.length === 0) {
        return (
            <DisconnectConfirmationMessage>
                {disconnectByUninstallingApp ? (
                    T._('To disconnect, remove the Zakeke app from your store.', 'Admin')
                ) : (
                    <>
                        {T._('Do you want to disconnect', 'Admin')}{' '}
                        <strong>
                            {storeDisplayName || (isMasterIntegration ? T._('this Master Integration', 'Admin') : T._('this Sales Channel', 'Admin'))}
                        </strong>
                        ?
                    </>
                )}
            </DisconnectConfirmationMessage>
        );
    }

    const affectedCount = products.length;
    const bannerTitle = isMasterIntegration
        ? T._('Important: products will break', 'Admin')
        : affectedCount === 1
          ? T._('1 product will be permanently deleted', 'Admin')
          : `${affectedCount} ${T._('products will be permanently deleted', 'Admin')}`;

    return (
        <>
            <DisconnectDangerBanner title={bannerTitle}>
                {isMasterIntegration ? (
                    T._(
                        'Products originating from or published on the Master Integration will no longer be available for customization on your store. They will remain in the product list and be marked as broken.',
                        'Admin'
                    )
                ) : (
                    <>
                        {T._('They were created from products of', 'Admin')} <strong>{storeDisplayName || T._('this store', 'Admin')}</strong>{' '}
                        {T._("and aren't linked to a product in any other sales channel.", 'Admin')}{' '}
                        {disconnectByUninstallingApp
                            ? T._("They'll be deleted when you uninstall the app, and this can't be undone.", 'Admin')
                            : T._("They'll be deleted when you disconnect, and this can't be undone.", 'Admin')}
                    </>
                )}
            </DisconnectDangerBanner>

            {isMasterIntegration ? (
                <LinkPanel>
                    <LinkPanelHeader>
                        <LinkPanelTitle>{T._('Products that will be marked as broken', 'Admin')}</LinkPanelTitle>
                    </LinkPanelHeader>
                    <ProductList>
                        {products.map((product) => (
                            <ProductItem key={product.id}>
                                <ProductThumb $src={product.smallImageUrl} />
                                <ProductInfo>
                                    {product.name.length > MAX_PRODUCT_NAME_LENGTH ? (
                                        <ZTooltip position='top' title={product.name}>
                                            <ProductName>{truncateProductName(product.name)}</ProductName>
                                        </ZTooltip>
                                    ) : (
                                        <ProductName>{product.name}</ProductName>
                                    )}
                                    <ProductMeta>
                                        {T._('ID', 'Admin')} {product.code}
                                    </ProductMeta>
                                </ProductInfo>
                            </ProductItem>
                        ))}
                    </ProductList>
                </LinkPanel>
            ) : (
                <StepsContainer>
                    <Step>
                        <StepNumber>1</StepNumber>
                        <StepBody>
                            <StepTitle>
                                {T._('Keep the products you need', 'Admin')} <StepTitleMuted>({T._('optional', 'Admin')})</StepTitleMuted>
                            </StepTitle>
                            <StepDescription>
                                {T._(
                                    "Link a product to a matching one in another store and it won't be deleted. You can do it here or later from Customizable Products.",
                                    'Admin'
                                )}{' '}
                                <Link href={HELP_URL} target='_blank'>
                                    {T._('Learn how linking works', 'Admin')}
                                </Link>
                            </StepDescription>
                            <LinkPanel>
                                <ProductList>
                                    {products.map((product) => (
                                        <ProductItem key={product.id}>
                                            <ProductThumb $src={product.smallImageUrl} />
                                            <ProductInfo>
                                                {product.name.length > MAX_PRODUCT_NAME_LENGTH ? (
                                                    <ZTooltip position='top' title={product.name}>
                                                        <ProductName>{truncateProductName(product.name)}</ProductName>
                                                    </ZTooltip>
                                                ) : (
                                                    <ProductName>{product.name}</ProductName>
                                                )}
                                                <ProductMeta>
                                                    {T._('ID', 'Admin')} {product.code}
                                                </ProductMeta>
                                            </ProductInfo>
                                            <LinkProductButton type='button' $size='small' onClick={() => handleAddToOnlineStore(product)}>
                                                {T._('Add to online store', 'Admin')}
                                            </LinkProductButton>
                                        </ProductItem>
                                    ))}
                                </ProductList>
                                <LinkPanelFooter>
                                    <DisconnectInlineLink type='button' onClick={handleViewDeletableProducts}>
                                        {affectedCount === 1
                                            ? T._('View 1 product in Customizable Products', 'Admin')
                                            : `${T._('View', 'Admin')} ${affectedCount} ${T._('products in Customizable Products', 'Admin')}`}
                                    </DisconnectInlineLink>
                                </LinkPanelFooter>
                            </LinkPanel>
                        </StepBody>
                    </Step>

                    {disconnectByUninstallingApp && (
                        <>
                            <StepDivider />
                            <Step>
                                <StepNumber>2</StepNumber>
                                <StepBody>
                                    <StepSplit>
                                        <StepText>
                                            <StepTitle>{T._('Disconnect the store', 'Admin')}</StepTitle>
                                            <StepDescription>
                                                {T._(
                                                    'This sales channel can only be disconnected by uninstalling the Zakeke app from the Shopify admin of',
                                                    'Admin'
                                                )}{' '}
                                                {storeDisplayName || T._('your store', 'Admin')}.{' '}
                                                {T._("Do this after step 1 — any product that isn't linked by then will be permanently deleted.", 'Admin')}
                                            </StepDescription>
                                        </StepText>
                                        <OpenAdminButton
                                            type='button'
                                            $size='small'
                                            onClick={() => window.open(getShopifyAdminUrl(storeDomain), '_blank', 'noopener,noreferrer')}
                                        >
                                            {T._('Open Shopify admin', 'Admin')}
                                            <Icon>
                                                <OpenInNewIcon />
                                            </Icon>
                                        </OpenAdminButton>
                                    </StepSplit>
                                </StepBody>
                            </Step>
                        </>
                    )}
                </StepsContainer>
            )}
        </>
    );
};

const SalesChannelDisconnectWarning: FC<SalesChannelDisconnectWarningProps> = (props) => {
    const { salesChannelID, storeLabel, disconnectByUninstallingApp = false } = props;
    const shouldLoadAffectedProducts = !disconnectByUninstallingApp || toLocalID(salesChannelID) !== 1;

    return (
        <DisconnectDialogContent>
            <SalesChannelDisconnectHeader {...props} />
            {shouldLoadAffectedProducts ? (
                <Suspense fallback={<DisconnectDialogLoader />}>
                    <SalesChannelDisconnectDetails {...props} />
                </Suspense>
            ) : (
                <DisconnectDangerBanner title={T._('Important: products will break', 'Admin')}>
                    {T._('To disconnect this Sales Channel, uninstall the Zakeke app from', 'Admin')}{' '}
                    <strong>{storeLabel || T._('this store', 'Admin')}</strong>.{' '}
                    {T._(
                        'Products originating from or published on it will no longer be available for customization and will be marked as broken in the product list.',
                        'Admin'
                    )}
                </DisconnectDangerBanner>
            )}
        </DisconnectDialogContent>
    );
};

const HELP_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels#h_01G5S8F7PSYM7H0MHD44K1G6FM';

const SalesChannels: FC<{}> = () => {
    const { openHelpGuide } = useInlineHelp();
    const navigate = useNavigate();
    const [fetchKey, setFetchKey] = useState(0);
    const [miniSitesFetchKey, setMiniSitesFetchKey] = useState(0);
    const { showDialog, closeDialog } = useDialogManager();
    const [isPending, startTransition] = useTransition();
    const { state } = useAppState();
    const { isAllowed } = usePermissions();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const added = searchParams.get('added') || 'false';
    const isMaster = searchParams.get('isMaster') || 'false';

    const query = useLazyLoadQuery<salesChannelsQuery>(
        graphql`
            query salesChannelsQuery {
                shopifyAppEmbedsToEnable {
                    salesChannelID
                }
                shopifyAuthEcommerceSettings {
                    isTestStore
                }
                ...salesChannelsActive
                subscription {
                    areSalesChannelsEnabled
                    planId
                }
                standaloneSites {
                    standaloneSiteID
                }
            }
        `,
        {},
        { fetchKey, fetchPolicy: 'network-only' }
    );

    const [setSalesChannelStatus] = useMutation<salesChannelSetStatusMutation>(graphql`
        mutation salesChannelSetStatusMutation($id: ID!, $status: SalesChannelStatus!) {
            setSalesChannelStatus(id: $id, status: $status) {
                taskID
            }
        }
    `);

    const [inlineRenameSalesChannel] = useMutation<renameSalesChannelDialogMutation>(updateNameSalesChannel);

    const pendingAppEmbeds = usePendingAppEmbeds(query.shopifyAppEmbedsToEnable ?? []);

    const [activeSalesChannels, refetch] = useRefetchableFragment<salesChannelsQuery, salesChannelsActive$key>(activeSalesChannelFragment, query);

    const isStandaloneEnabled = useFeatureFlag('standalone');

    const isSwitchSalesChannelAvailable = useFeatureFlagWithConditions('switchSalesChannel', {
        userId: activeSalesChannels.user.userID.toString()
    });

    const masterSalesChannel = activeSalesChannels.salesChannels.filter(
        (channel) => toLocalID(channel.id) == 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE'
    );

    const otherSalesChannel = activeSalesChannels.salesChannels.filter(
        (channel) => toLocalID(channel.id) != 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE'
    );

    const masterEcommerceSettings = masterSalesChannel[0]?.ecommerceSettings;

    // Shopify sales channels require a Shopify Master Integration: promoting a non-Shopify channel would leave the current
    // Shopify master (and any other Shopify store) under a non-Shopify Master Integration.
    const isShopifyMaster = masterEcommerceSettings?.ecommerceName === 'SHOPIFY';

    const productSwitcherTasks = activeSalesChannels.productSwitcherTasks || [];
    const [pendingDisconnectTaskIDsBySalesChannelID, setPendingDisconnectTaskIDsBySalesChannelID] = useState<Record<string, number>>({});
    const disconnectTaskIDsBySalesChannelID = productSwitcherTasks.reduce<Record<string, number>>(
        (taskIDsBySalesChannelID, task) => {
            const sourceSalesChannelID = task.extraData?.sourceSalesChannelID;
            if (
                task.taskType === DISCONNECT_SALES_CHANNEL_TASK_TYPE &&
                (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing) &&
                sourceSalesChannelID != null
            ) {
                taskIDsBySalesChannelID[sourceSalesChannelID.toString()] = task.taskID;
            }

            return taskIDsBySalesChannelID;
        },
        { ...pendingDisconnectTaskIDsBySalesChannelID }
    );
    const isSellerMigrating = activeSalesChannels.isSellerMigrating ?? false;
    const hasPendingPaymentMethodSetup =
        isSellerMigrating &&
        productSwitcherTasks.some(
            (task) => task.taskType === 'EDITSUBSCRIPTION' && task.state === TASKSTATUS.Completed && task.extraData?.paymentMethodSetupTaskID != null
        );

    // Every pending task of the switch flow (plan preparation, subscription change, Shopify charges, the switch itself) —
    // not a sales channel disconnect, which is not part of it.
    const isSwitchFlowTaskPending = (task: (typeof productSwitcherTasks)[number]) =>
        task.taskType !== DISCONNECT_SALES_CHANNEL_TASK_TYPE && isTaskPending(task.state);

    // While the switch flow runs the action stays visible but disabled, so the seller sees why it cannot start another one.
    // A pending payment method setup is the exception: clicking the action is how the seller resumes that step.
    const isSwitchFlowInProgress = !hasPendingPaymentMethodSetup && productSwitcherTasks.some(isSwitchFlowTaskPending);

    const canShowSwitchButton =
        (!config.isProduction || isSwitchSalesChannelAvailable) &&
        (hasPendingPaymentMethodSetup ||
            productSwitcherTasks.every(
                (task) =>
                    isSwitchFlowTaskPending(task) ||
                    (!isTaskPending(task.state) && (task.state !== TASKSTATUS.Error || task.taskType === CREATE_CUSTOM_PLAN_FOR_SELLER_TASK_TYPE))
            ));

    const headings = [
        ['#', SALES_CHANNEL_TABLE_WIDTHS[0]],
        [T._('Name', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[1]],
        [T._('Url', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[2]],
        [T._('Platform', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[3]],
        ['', SALES_CHANNEL_TABLE_WIDTHS[4]]
    ] as TableHeading[];

    const buildRowCells = (salesChannel: salesChannelsActive$data['salesChannels'][number]) => {
        const isZakekeOff =
            salesChannel.ecommerceSettings?.ecommerceName === 'SHOPIFY' &&
            pendingAppEmbeds.some((embed) => isSameSalesChannel(embed.salesChannelID, salesChannel.id));
        const name: string = salesChannel.detail?.properties?.name || '';

        const storeUrl = salesChannel.ecommerceSettings?.definitiveUrl || '';

        const storeNumber = getStoreNumber(salesChannel.id) || 1;
        const isMasterSalesChannel = toLocalID(salesChannel.id) === 1;
        const localSalesChannelID = toLocalID(salesChannel.id)?.toString();
        const disconnectTaskID = localSalesChannelID ? disconnectTaskIDsBySalesChannelID[localSalesChannelID] : undefined;
        const isDisconnecting = disconnectTaskID != null;

        const cannotDisconnect = isMasterSalesChannel;
        const disconnectByUninstallingApp = ['SHOPIFY', 'BIG_COMMERCE', 'WIX'].includes(salesChannel.ecommerceSettings?.ecommerceName || 'NONE');
        const masterDisconnectTooltip = disconnectByUninstallingApp
            ? { title: T._('To disconnect, remove the app from your store.', 'Admin') }
            : { html: <MasterDisconnectTooltip />, interactive: true };

        return [
            <StyledDivElementTable>
                <StoreNumberCell>
                    {T._(`Store ${storeNumber}`, 'Admin')}
                    <StoreBadges>
                        {salesChannel.ecommerceSettings?.ecommerceName === 'SHOPIFY' &&
                            (isMasterSalesChannel ? (
                                query.shopifyAuthEcommerceSettings?.isTestStore && <Badge variant='warning'>{T._('Development', 'Admin')}</Badge>
                            ) : (
                                <CustomErrorBoundary render={() => null}>
                                    <Suspense fallback={null}>
                                        <ShopifyDevelopmentBadge salesChannelID={salesChannel.id} />
                                    </Suspense>
                                </CustomErrorBoundary>
                            ))}
                        {isMasterSalesChannel && <Badge variant='info'>{T._('Master', 'Admin')}</Badge>}
                        {isZakekeOff && (
                            <ZTooltip position='top' title={getZakekeOffTooltip()}>
                                <ZakekeOffBadgeButton type='button' onClick={scrollToShopifyAppEmbedBanner}>
                                    <Badge variant='danger'>{getZakekeOffLabel()}</Badge>
                                </ZakekeOffBadgeButton>
                            </ZTooltip>
                        )}
                    </StoreBadges>
                </StoreNumberCell>
            </StyledDivElementTable>,

            <InlineRenameCell name={name} onRename={(newName) => handleInlineRenameSalesChannel(salesChannel, newName)} />,
            <StyledDivElementTable>
                <StyledLinkContainer>
                    <StyledLink href={storeUrl} target='_blank' title={storeUrl}>
                        <span>{storeUrl}</span>
                    </StyledLink>
                </StyledLinkContainer>
            </StyledDivElementTable>,
            <StyledDivElementTable>
                <StyledImg>{SalesChannelIcon(salesChannel.ecommerceSettings?.ecommerceName, false)}</StyledImg>
            </StyledDivElementTable>,
            <ActionsColContainer>
                {isDisconnecting ? (
                    <ZTooltip position='top' title={T._('This sales channel is being disconnected.', 'Admin')}>
                        <Badge variant='info'>{T._('Disconnecting', 'Admin')}</Badge>
                    </ZTooltip>
                ) : !cannotDisconnect ? (
                    <ZTooltip position={'top'} title={T._('Disconnect', 'Admin')}>
                        <GhostButton
                            $size='small'
                            $type='icon'
                            aria-label={T._('Disconnect', 'Admin')}
                            onClick={() => handleDeactivateSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE')}
                        >
                            <Icon>
                                <LinkOffIcon />
                            </Icon>
                        </GhostButton>
                    </ZTooltip>
                ) : (
                    <ZTooltip position='left' {...masterDisconnectTooltip}>
                        <UnclickableDisconnectButtonContainer>
                            <GhostButton $size='small' $type='icon' disabled aria-label={T._('Disconnect', 'Admin')}>
                                <Icon>
                                    <LinkOffIcon />
                                </Icon>
                            </GhostButton>
                        </UnclickableDisconnectButtonContainer>
                    </ZTooltip>
                )}
                <DropdownActions
                    header={
                        <Icon $hoverable>
                            <VerticalThreePoint />
                        </Icon>
                    }
                    sections={[
                        [
                            storeUrl && (
                                <DropdownActionItem key={'view_store_button_' + salesChannel.id} onClick={() => window.open(storeUrl, '_blank')}>
                                    {T._('View store', 'Admin')}
                                </DropdownActionItem>
                            ),
                            <DropdownActionItem
                                key={'API_key_button_' + salesChannel.id}
                                onClick={() => {
                                    handleIntegrationSettingsAndApiKey(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE');
                                }}
                            >
                                {['API', 'C_CLOUD'].includes(salesChannel.ecommerceSettings!.ecommerceName)
                                    ? T._('Integration settings and API Keys', 'Admin')
                                    : T._('API Keys', 'Admin')}
                            </DropdownActionItem>,
                            ,
                            salesChannel.ecommerceSettings?.ecommerceName === 'PRESTA_SHOP' && (
                                <DropdownActionItem
                                    key={'edit_store_url_button_' + salesChannel.id}
                                    onClick={() => {
                                        handleChangeStoreUrlClick(salesChannel);
                                    }}
                                >
                                    {T._('Change store URL', 'Admin')}
                                </DropdownActionItem>
                            ),
                            salesChannel.ecommerceSettings?.ecommerceName &&
                                !['API', 'C_CLOUD', 'NONE'].includes(salesChannel.ecommerceSettings.ecommerceName) && (
                                    <DropdownActionItem
                                        key={'integration_settings_button_' + salesChannel.id}
                                        onClick={() => {
                                            const platform = salesChannel.ecommerceSettings!.ecommerceName;
                                            const url =
                                                platform === 'SHOPIFY'
                                                    ? backofficeUrl(`/settings/integration-settings/SHOPIFY?salesChannelID=${toLocalID(salesChannel.id)}`)
                                                    : backofficeUrl(`/settings/integration-settings/${platform}`);
                                            navigate(url);
                                        }}
                                    >
                                        {T._('Go to Integration Settings', 'Admin')}
                                    </DropdownActionItem>
                                ),
                            hasMasterEcommerce &&
                                canShowSwitchButton &&
                                // Payment step disabled — a pending PAYMENT_CONFIRMED task no longer keeps this action visible.
                                // productSwitcherTasks.some(
                                //     (task) =>
                                //         task.taskType === 'PAYMENT_CONFIRMED' &&
                                //         isTaskPending(task.state) &&
                                //         task.extraData.secondarySalesChannelID?.toString() === toLocalID(salesChannel.id)?.toString()
                                // ) &&
                                salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' &&
                                salesChannel.id !== 'guid://Zakeke/SalesChannel/1' && (
                                    <SetAsMasterActionItem
                                        key={'switch_sales_channel' + salesChannel.id}
                                        salesChannelID={salesChannel.id}
                                        ecommerceName={salesChannel.ecommerceSettings?.ecommerceName || 'NONE'}
                                        isChecking={checkingSellerPlanForChannelID === salesChannel.id}
                                        disabled={checkingSellerPlanForChannelID !== null || isDisconnecting}
                                        disabledReason={
                                            isSwitchFlowInProgress
                                                ? T._(
                                                      'A sales channel switch is in progress. You can set a new Master Integration once it is complete.',
                                                      'Admin'
                                                  )
                                                : isShopifyMaster && salesChannel.ecommerceSettings?.ecommerceName !== 'SHOPIFY'
                                                  ? T._(
                                                        'Your Master Integration is Shopify. Shopify sales channels can only be connected to a Shopify Master Integration, so a non-Shopify sales channel cannot be set as Master Integration.',
                                                        'Admin'
                                                    )
                                                  : undefined
                                        }
                                        onClick={() => {
                                            handleSwitchSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE');
                                        }}
                                    />
                                )
                        ],
                        [
                            cannotDisconnect ? (
                                <ZTooltip key={'disconnect_' + salesChannel.id} addContainer position='left' {...masterDisconnectTooltip}>
                                    <DropdownActionItem danger disabled>
                                        {T._('Disconnect', 'Admin')}
                                    </DropdownActionItem>
                                </ZTooltip>
                            ) : (
                                <DropdownActionItem
                                    key={'disconnect_' + salesChannel.id}
                                    danger
                                    disabled={isDisconnecting}
                                    onClick={() => handleDeactivateSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE')}
                                >
                                    {T._('Disconnect', 'Admin')}
                                </DropdownActionItem>
                            )
                        ]
                    ]}
                    orientation={'RIGHT'}
                />
            </ActionsColContainer>
        ];
    };

    const storeNumbers = generateStoreNumbersByPosition(activeSalesChannels.salesChannels.filter((x) => x.ecommerceSettings?.ecommerceName !== 'NONE'));
    const getStoreNumber = (salesChannelID: string) => storeNumbers.find((x) => x.salesChannelID === salesChannelID)?.storeNumber ?? 0;

    const canShowAddSalesChannelDialog = searchParams.get('showAddSalesChannelDialog') || 'false';

    const isFirstSalesChannel = !masterEcommerceSettings?.ecommerceGlobalID;

    const showBackButton = !(masterSalesChannel?.length > 0 && otherSalesChannel?.length == 0 && added === 'true');

    useEffect(() => {
        if (added === 'true' && !localStorage.getItem('pendingAction')) {
            showSuccessNotification({
                message: isMaster ? T._('Your Integration has been saved.', 'Admin') : T._('The updated list of sales channels will be shown soon', 'Admin'),
                dismiss: { duration: 5000 }
            });

            const timerId = setTimeout(() => {
                startTransition(() => setFetchKey((prev) => prev + 1));
                navigate(backofficeUrl('/sales-channels'), { replace: true });
            }, 5000);

            return () => clearTimeout(timerId);
        }
    }, [added, isMaster, location.key]);

    useEffect(() => {
        if (canShowAddSalesChannelDialog === 'true') {
            handleAddSaleChannel();
        }
    }, [canShowAddSalesChannelDialog]);

    useEffect(() => {
        // trigger every time the list of channels changes
        const pending = JSON.parse(localStorage.getItem('pendingAction') ?? 'null');
        if (!pending || pending.type !== 'publish-product') return;

        const integrationDone = activeSalesChannels.salesChannels.some((sc) => sc.ecommerceSettings?.ecommerceName !== 'NONE');
        if (!integrationDone) return;

        if (Date.now() - pending.ts > 10 * 60_000) {
            localStorage.removeItem('pendingAction');
            return;
        }

        localStorage.removeItem('pendingAction');

        navigate(backofficeUrl(pending.redirectPage), {
            replace: true,
            state: { doPublishImmediately: true, productID: pending.productId }
        });
    }, [activeSalesChannels.salesChannels]);

    // Declared here, not at the top of the component, so the switch flow's effects still run after the page's own.
    // Effect order decides dialog stacking: the reducer appends to an array that renders in order.
    const { checkingSellerPlanForChannelID, handleSwitchSalesChannel } = useSalesChannelSwitch({
        activeSalesChannels,
        productSwitcherTasks,
        isSellerMigrating,
        masterEcommerceSettings,
        planId: query.subscription?.planId,
        searchParams,
        setSearchParams,
        refetch
    });

    const handleAddSaleChannel = () => {
        if (isFirstSalesChannel) {
            navigate(backofficeUrl('/connect-shop'));
            return;
        }

        query.shopifyAuthEcommerceSettings?.isTestStore && config.isProduction
            ? showDialog(
                  'add-sales-channel-not-available',
                  <MessageDialog
                      windowDecorator={SalesChannelNotAvailableDialogWindow}
                      message={
                          <>
                              {T._(
                                  "We're sorry, but you cannot add sales channels at this time. Your account is currently in trial mode on Shopify or is a development store.",
                                  'Admin'
                              )}
                              <br />
                              <br />
                              {T._('To access this feature, please upgrade to an active Shopify plan.', 'Admin')}
                              <br />
                              <br />
                              {T._('Thank you for your understanding.', 'Admin')}
                          </>
                      }
                      buttons={[
                          {
                              label: T._('OK', 'Admin'),
                              onClick: () => {
                                  closeDialog('add-sales-channel-not-available');
                              }
                          }
                      ]}
                  />
              )
            : showDialog(
                  'add-sales-channel',
                  <AddSalesChannelDialog
                      onSalesChannelCreated={() => {
                          closeDialog('add-sales-channel');
                          setFetchKey((prev) => prev + 1);
                      }}
                  ></AddSalesChannelDialog>
              );
    };

    const handleCreateMiniSite = () => {
        const eligibleChannels = activeSalesChannels.salesChannels.filter(
            (sc) => sc.ecommerceSettings?.ecommerceName === 'NONE' || sc.ecommerceSettings?.ecommerceName === 'SHOPIFY'
        );

        showDialog(
            'create-standalone-site',
            <CreateStandaloneSiteDialog
                salesChannels={eligibleChannels}
                onCreated={(site) => {
                    closeDialog('create-standalone-site');
                    navigate(backofficeUrl(`/mini-sites/${encodeURIComponent(site.standaloneSiteID.toString())}/settings`));
                }}
            />
        );
    };

    const handleDeactivateSalesChannel = (id: string, ecommerce: ECommerce) => {
        if (toLocalID(id) === 1) return;

        const channel = activeSalesChannels.salesChannels.find((c) => c.id === id);
        const storeName = channel?.detail?.properties?.name || '';
        const storeDomain = channel?.ecommerceSettings?.definitiveUrl || channel?.ecommerceSettings?.remoteUrl || '';
        const storeLabel = storeName || storeDomain || '';
        const storeNumber = getStoreNumber(id);
        const storeTitle = storeNumber ? `${T._('Store', 'Admin')} ${storeNumber}` : storeLabel;

        if (ecommerce === 'SHOPIFY' || ecommerce === 'BIG_COMMERCE') {
            showDialog(
                'disconnect-channel',
                <Dialog windowDecorator={DisconnectDialogWindow} minHeight={460} onClose={() => closeDialog('disconnect-channel')}>
                    <SalesChannelDisconnectWarning
                        salesChannelID={id}
                        storeLabel={storeLabel}
                        storeTitle={storeTitle}
                        storeName={storeName}
                        storeDomain={storeDomain}
                        disconnectByUninstallingApp
                    />
                </Dialog>
            );
        } else {
            showDialog(
                'disconnect-channel',
                <Dialog
                    onClose={() => closeDialog('disconnect-channel')}
                    buttons={[
                        {
                            label: T._('Disconnect', 'Admin'),
                            destructive: true,
                            onClick: () => {
                                closeDialog('disconnect-channel');

                                const loadingNotification = showNotification({
                                    type: 'default',
                                    message: T._(`Disconnecting sales channel...`, 'Admin')
                                });

                                setSalesChannelStatus({
                                    variables: {
                                        id: id,
                                        status: 'DISABLED'
                                    },
                                    onError: () => {
                                        removeNotification(loadingNotification);
                                        showErrorNotification({ message: T._('Failed disconnect sales channel', 'Admin') });
                                    },
                                    onCompleted: (response) => {
                                        removeNotification(loadingNotification);
                                        const taskID = response.setSalesChannelStatus?.taskID;
                                        if (taskID == null) {
                                            showErrorNotification({ message: T._('Failed to start the sales channel disconnection.', 'Admin') });
                                            return;
                                        }

                                        const localSalesChannelID = toLocalID(id);
                                        if (localSalesChannelID != null) {
                                            setPendingDisconnectTaskIDsBySalesChannelID((previousTaskIDs) => ({
                                                ...previousTaskIDs,
                                                [localSalesChannelID.toString()]: taskID
                                            }));
                                        }

                                        showSuccessNotification({ message: T._('Sales channel disconnection started.', 'Admin') });
                                        refetch({}, { fetchPolicy: 'network-only' });
                                    }
                                });
                            }
                        }
                    ]}
                >
                    <SalesChannelDisconnectWarning
                        salesChannelID={id}
                        storeLabel={storeLabel}
                        storeTitle={storeTitle}
                        storeName={storeName}
                        storeDomain={storeDomain}
                    />
                </Dialog>
            );
        }
    };

    const handleInlineRenameSalesChannel = (salesChannel: salesChannelsActive$data['salesChannels'][number], newName: string) => {
        inlineRenameSalesChannel({
            variables: {
                input: {
                    id: salesChannel.detail?.id,
                    salesChannelGlobalID: salesChannel.id,
                    properties: { name: newName }
                }
            },
            updater: (store) => {
                const salesChannelRecord = store.get(salesChannel.id);
                const detailRecord = store.getRootField('upsertSalesChannelDetail');

                if (salesChannelRecord && detailRecord) salesChannelRecord.setLinkedRecord(detailRecord, 'detail');
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed rename sales channel', 'Admin') });
            }
        });
    };

    const handleIntegrationSettingsAndApiKey = (id: string, ecommerce: string) => {
        if (ecommerce === 'API') {
            showDialog('api-connection', <ApiIntegrationSalesChannelDialog salesChannelID={id || ''} hasMasterIntegration={true} />);
        } else if (ecommerce === 'C_CLOUD') {
            showDialog('sales-force', <CCloudIntegrationSalesChannelDialog salesChannelID={id || ''} />);
        } else {
            showDialog('api-key', <ApiKeyDialog salesChannelID={id} />);
        }
    };

    const handleChangeStoreUrlClick = (salesChannel: salesChannelsActive$data['salesChannels'][0]) => {
        showDialog('store-url-sales-channel', <StoreUrlSalesChannelDialog salesChannel={salesChannel} />);
    };

    // With switch tasks the page splits into two tabs, like Pre-designed Templates (Products / Duplication Tasks).
    const hasSwitchTasks = isAllowed(['OWNER']) && productSwitcherTasks.some((task) => task.taskType === 'SWITCHSALESCHANNELS');
    const activeTab = hasSwitchTasks && searchParams.get('tab') === 'tasks' ? 1 : 0;

    const setActiveTab = (index: number) => {
        setSearchParams((currentParams) => {
            const nextParams = new URLSearchParams(currentParams);
            if (index === 1) nextParams.set('tab', 'tasks');
            else nextParams.delete('tab');
            return nextParams;
        });
    };

    const renderWithSwitchTasksTabs = (salesChannelsContent: ReactNode) =>
        hasSwitchTasks ? (
            <ZTabs selectedIndex={activeTab}>
                <TabListContainer>
                    <TabList>
                        <Tab onClick={() => setActiveTab(0)}>{T._('Sales Channels', 'Admin')}</Tab>
                        <Tab onClick={() => setActiveTab(1)}>{T._('Tasks', 'Admin')}</Tab>
                    </TabList>
                </TabListContainer>

                <TabPanel>{salesChannelsContent}</TabPanel>

                <TabPanel>
                    <SalesChannelSwitchTasksPanel />
                </TabPanel>
            </ZTabs>
        ) : (
            salesChannelsContent
        );

    const hasMasterEcommerce = !!(masterEcommerceSettings?.ecommerceName && masterEcommerceSettings.ecommerceName !== 'NONE');
    const hasOtherSalesChannels = otherSalesChannel.length > 0;

    const miniSitesCount = isStandaloneEnabled ? (query.standaloneSites?.length ?? 0) : 0;

    // Show the section layout (with empty states) as soon as there is anything to manage.
    // Keep the two-card onboarding hero only when neither an online store nor a mini-site exists.
    const showSections = hasMasterEcommerce || hasOtherSalesChannels || miniSitesCount > 0;

    const pageHeaderDescription = isStandaloneEnabled
        ? T._('Connect your online stores and launch standalone mini-sites, then manage all your sales channels from a single place.', 'Admin')
        : T._('Connect your online stores and manage all your sales channels from a single place.', 'Admin');

    const heroDescription = isStandaloneEnabled
        ? T._(
              'Connect Zakeke to your online store or marketplace, or launch a standalone mini-site, and offer product customization wherever you sell.',
              'Admin'
          )
        : T._('Connect Zakeke to your online store or marketplace and offer product customization wherever you sell.', 'Admin');

    // Rows follow the store numbers, so a Master Integration switch only moves the Master label.
    const allStores = [...masterSalesChannel, ...otherSalesChannel].sort((a, b) => getStoreNumber(a.id) - getStoreNumber(b.id));

    const handleDisconnectTaskFinished = (salesChannelID: string, taskID: number, state: number, lastError: string | null | undefined) => {
        setPendingDisconnectTaskIDsBySalesChannelID((previousTaskIDs) => {
            if (previousTaskIDs[salesChannelID] !== taskID) return previousTaskIDs;

            const { [salesChannelID]: _, ...remainingTaskIDs } = previousTaskIDs;
            return remainingTaskIDs;
        });

        if (state === TASKSTATUS.Error) {
            showErrorNotification({
                message: lastError || T._('The sales channel disconnection could not be completed.', 'Admin')
            });
        }

        setFetchKey((previousFetchKey) => previousFetchKey + 1);
    };

    return (
        <StyledContainerBase showBackButton={showBackButton}>
            {Object.entries(disconnectTaskIDsBySalesChannelID).map(([salesChannelID, taskID]) => (
                <SalesChannelDisconnectTaskPoller
                    key={taskID}
                    taskID={taskID}
                    onFinished={(state, lastError) => handleDisconnectTaskFinished(salesChannelID, taskID, state, lastError)}
                />
            ))}
            {showSections && (
                <PageHeader
                    title={T._('Sales Channels', 'Admin')}
                    pageKey='sales-channels'
                    backLabel={T._('Back', 'Admin')}
                    description={<>{pageHeaderDescription}</>}
                    learnMoreLabel={T._('Learn more', 'Admin')}
                    learnMoreUrl={HELP_URL}
                    onLearnMoreClick={() => openHelpGuide(HELP_URL)}
                    isBackVisible={showBackButton}
                />
            )}

            <Stack $gap='16px'>
                <ShopifyAppEmbedBanner />
                {showSections ? (
                    <>
                        {added !== 'false' && isPending && Loader}
                        {!isPending &&
                            renderWithSwitchTasksTabs(
                                <Stack>
                                    {hasMasterEcommerce ? (
                                        <CompactSection
                                            title={T._('Online Stores', 'Admin')}
                                            description={T._(
                                                'Online stores are e-commerce platforms connected to Zakeke. The store marked as Master controls billing and product sync for all channels.',
                                                'Admin'
                                            )}
                                            headerActions={
                                                <SectionHeaderActionsGroup>
                                                    {masterEcommerceSettings!.ecommerceName !== 'AMAZON' && (
                                                        <ZTooltip position='bottom' title={T._('Integration Settings', 'Admin')}>
                                                            <GhostButton
                                                                $size='small'
                                                                $type='icon'
                                                                aria-label={T._('Integration Settings', 'Admin')}
                                                                onClick={() => navigate(backofficeUrl('/settings/integration-settings'))}
                                                            >
                                                                <Icon>
                                                                    <CogIcon />
                                                                </Icon>
                                                            </GhostButton>
                                                        </ZTooltip>
                                                    )}
                                                    {masterEcommerceSettings!.ecommerceName !== 'AMAZON' && <HeaderActionsDivider />}
                                                    <PrimaryButton
                                                        $size='medium'
                                                        disabled={state.lastUsedTool === 2}
                                                        onClick={() => handleAddSaleChannel()}
                                                        style={{ minWidth: '160px' }}
                                                    >
                                                        <Icon>
                                                            <PlusIcon />
                                                        </Icon>
                                                        {T._('Add Online Store', 'Admin')}
                                                    </PrimaryButton>
                                                </SectionHeaderActionsGroup>
                                            }
                                        >
                                            {state.lastUsedTool === 2 && otherSalesChannel.length > 0 ? (
                                                <ZTooltip
                                                    position={'top'}
                                                    title={T._('Additional Sales Channels are not supported for 3D Configurator', 'Admin')}
                                                >
                                                    <DisabledSalesChannelContainer>
                                                        <Table headings={headings} compact>
                                                            {allStores.map((salesChannel, i) => (
                                                                <Row key={i.toString()} cells={buildRowCells(salesChannel)} />
                                                            ))}
                                                        </Table>
                                                    </DisabledSalesChannelContainer>
                                                </ZTooltip>
                                            ) : (
                                                <Table headings={headings} compact>
                                                    {allStores.map((salesChannel, i) => (
                                                        <Row key={i.toString()} cells={buildRowCells(salesChannel)} />
                                                    ))}
                                                </Table>
                                            )}
                                        </CompactSection>
                                    ) : hasOtherSalesChannels ? (
                                        <CompactSection
                                            title={T._('Online Stores', 'Admin')}
                                            description={T._(
                                                'Online stores are e-commerce platforms connected to Zakeke. The store marked as Master controls billing and product sync for all channels.',
                                                'Admin'
                                            )}
                                        >
                                            <Stack>
                                                <NotificationBanner
                                                    type='WARNING'
                                                    title={T._('Action required: add a Master Integration', 'Admin')}
                                                    content={T._(
                                                        'Your account requires a Master Integration in order to work properly. Add it before managing your Sales Channels.',
                                                        'Admin'
                                                    )}
                                                    buttons={[
                                                        {
                                                            label: T._('Add Master Integration', 'Admin'),
                                                            disabled: state.lastUsedTool === 2,
                                                            onClick: handleAddSaleChannel
                                                        }
                                                    ]}
                                                />
                                                {state.lastUsedTool === 2 ? (
                                                    <ZTooltip
                                                        position={'top'}
                                                        title={T._('Additional Sales Channels are not supported for 3D Configurator', 'Admin')}
                                                    >
                                                        <DisabledSalesChannelContainer>
                                                            <Table headings={headings} compact>
                                                                {allStores.map((salesChannel, i) => (
                                                                    <Row key={i.toString()} cells={buildRowCells(salesChannel)} />
                                                                ))}
                                                            </Table>
                                                        </DisabledSalesChannelContainer>
                                                    </ZTooltip>
                                                ) : (
                                                    <Table headings={headings} compact>
                                                        {allStores.map((salesChannel, i) => (
                                                            <Row key={i.toString()} cells={buildRowCells(salesChannel)} />
                                                        ))}
                                                    </Table>
                                                )}
                                            </Stack>
                                        </CompactSection>
                                    ) : (
                                        <CompactSection title={T._('Online Stores', 'Admin')}>
                                            <CompactEmptyState>
                                                <EmptyList
                                                    hideIcon
                                                    title={''}
                                                    subTitle={T._(
                                                        'Connect an online store or marketplace and embed Zakeke in your storefront as part of the buying experience.',
                                                        'Admin'
                                                    )}
                                                    buttonTitle={T._('Add Online Store', 'Admin')}
                                                    disabledButton={state.lastUsedTool === 2}
                                                    onButtonClick={() => handleAddSaleChannel()}
                                                />
                                            </CompactEmptyState>
                                        </CompactSection>
                                    )}

                                    {isStandaloneEnabled && (
                                        <Suspense
                                            fallback={
                                                <CompactSection title={T._('Mini-Sites', 'Admin')}>
                                                    <Center>
                                                        <Spinner />
                                                    </Center>
                                                </CompactSection>
                                            }
                                        >
                                            <MiniSitesList
                                                fetchKey={miniSitesFetchKey}
                                                onChanged={() => {
                                                    setMiniSitesFetchKey((prev) => prev + 1);
                                                    setFetchKey((prev) => prev + 1);
                                                }}
                                                onAdd={() => handleCreateMiniSite()}
                                            />
                                        </Suspense>
                                    )}
                                </Stack>
                            )}
                    </>
                ) : (
                    <>
                        {added !== 'false' && Loader}
                        {added === 'false' && (
                            <>
                                <HeroSection>
                                    <HeroTitle>{T._('Bring Zakeke to your customers', 'Admin')}</HeroTitle>
                                    <HeroDescription>{heroDescription}</HeroDescription>
                                </HeroSection>

                                <ChannelCardsGrid $singleColumn={!isStandaloneEnabled}>
                                    <ChannelCard>
                                        <CardBadge $variant='store'>{T._('Store', 'Admin')}</CardBadge>
                                        <CardTitle>{T._('Connect a Store', 'Admin')}</CardTitle>
                                        <CardText>
                                            {T._(
                                                'Connect an online e-commerce or marketplace, B2C or B2B — Shopify, WooCommerce, Magento and more. Zakeke is embedded in your store as part of the buying experience.',
                                                'Admin'
                                            )}
                                        </CardText>
                                        <FeatureList>
                                            <FeatureItem>{T._('Online store or marketplace, B2C or B2B', 'Admin')}</FeatureItem>
                                            <FeatureItem>{T._('Zakeke embedded in your storefront', 'Admin')}</FeatureItem>
                                            <FeatureItem>{T._('Part of the purchase experience', 'Admin')}</FeatureItem>
                                            <FeatureItem>{T._('Code-free connectors or API', 'Admin')}</FeatureItem>
                                            <FeatureItem>{T._('Automatic Product Sync', 'Admin')}</FeatureItem>
                                            <FeatureItem>{T._('Real-time Order Management', 'Admin')}</FeatureItem>
                                        </FeatureList>
                                        <CardAction>
                                            <FullWidthPrimaryButton $size='medium' onClick={() => handleAddSaleChannel()}>
                                                {T._('Connect Store', 'Admin')}
                                            </FullWidthPrimaryButton>
                                        </CardAction>
                                    </ChannelCard>

                                    {isStandaloneEnabled && (
                                        <ChannelCard>
                                            <CardBadge $variant='portal'>{T._('Mini-Site', 'Admin')}</CardBadge>
                                            <CardTitle>{T._('Create a Mini-Site', 'Admin')}</CardTitle>
                                            <CardText>
                                                {T._(
                                                    'Launch a standalone customizer portal hosted by us. Ideal for kiosks, showrooms, B2B sales, and proofing.',
                                                    'Admin'
                                                )}
                                            </CardText>
                                            <FeatureList>
                                                <FeatureItem>{T._('Ideal for kiosk & showroom experiences', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Great for B2B sales & proofing', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Customizable Theme', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Custom Sub-domain', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Products List & Collections', 'Admin')}</FeatureItem>
                                            </FeatureList>
                                            <CardAction>
                                                <FullWidthPrimaryButton $size='medium' onClick={() => handleCreateMiniSite()}>
                                                    {T._('Create Mini-Site', 'Admin')}
                                                </FullWidthPrimaryButton>
                                            </CardAction>
                                        </ChannelCard>
                                    )}
                                </ChannelCardsGrid>

                                {isStandaloneEnabled && (
                                    <InfoBanner>
                                        <InfoBannerContent>
                                            <InfoIcon>i</InfoIcon>
                                            <InfoBannerTextBlock>
                                                <strong>{T._('Need help deciding?', 'Admin')}</strong>
                                                <div>{T._('Compare the features of stores versus standalone mini-sites in our documentation.', 'Admin')}</div>
                                            </InfoBannerTextBlock>
                                        </InfoBannerContent>
                                        <TextButton onClick={() => openHelpGuide(HELP_URL)}>{T._('Read Guide', 'Admin')}</TextButton>
                                    </InfoBanner>
                                )}
                            </>
                        )}
                    </>
                )}
            </Stack>
        </StyledContainerBase>
    );
};

export default SalesChannels;
